import { Appearance, Size } from '@Components/designConstants';
import '@Pages/GeoMMM/ScenarioPlanning/Modals/RunScenarioProgressDialog.scss';
import cn from 'classnames';
import { ReactElement, ReactNode } from 'react';
import { Button } from '@Components/Button';
import { Spinner } from '@Components/Spinner';
import { Brand60 } from '@src/utils/colors';

interface RunScenarioProgressDialogProps {
  show: boolean;
  percent: number | null;
  title?: string;
  description?: ReactNode;
  // When set, the backdrop is solid white (like the default FullPageSpinner this dialog
  // replaces on page load); otherwise it's the translucent white of the transparent variant.
  solidBackdrop?: boolean;
  // When provided, renders the "Back to Editing" footer action; omit for flows with
  // nothing to go back to (e.g. the initial baseline load).
  onBackToEditing?: () => void;
}

/**
 * Progress overlay shown while a scenario planning lambda runs. The percent comes from
 * interim cometProgress payloads polled from S3; until the first one arrives (or for
 * models that don't report progress) the bar sits at 0%.
 *
 * Rendered as an absolutely-positioned overlay that fills the page body (not a portaled
 * Modal), so only the app sidebar (.navbar) and page header/tabs stay visible and the
 * whole body — full-page-router, toolbar, search bar — is covered during load.
 */
export const RunScenarioProgressDialog = ({
  show,
  percent,
  title = 'Running Scenario',
  description = (
    <>
      Remodeling outcomes based on your changes.
      <br />
      This will take a minute.
    </>
  ),
  solidBackdrop,
  onBackToEditing
}: RunScenarioProgressDialogProps): ReactElement | null => {
  if (!show) return null;

  return (
    <div className='run-scenario-progress-overlay'>
      <div className={cn('run-scenario-progress-backdrop', { solid: solidBackdrop })} />
      <div className='run-scenario-progress-card'>
        <div className='dialog-content'>
          <h4 className='dialog-title'>{title}</h4>
          <div className='dialog-body'>{description}</div>
          <div className='progress-track'>
            <div className='progress-fill' style={{ width: `${percent ?? 0}%` }} />
          </div>
          <div className='progress-status'>
            <Spinner size={20} color={Brand60} />
            <span>{percent ?? 0}% complete</span>
          </div>
        </div>
        {onBackToEditing && (
          <div className='dialog-footer'>
            <Button size={Size.LG} onClick={onBackToEditing} appearance={Appearance.EMPTY}>
              Back to Editing
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
